// RU-POSSUMMARY (PS-7): open (unrealised) and closed (realised) positions, paper and live,
// counted by the sign of their P&L. Read-only; fetches /api/analytics/position-summary itself.
// RU-POSFU (PF-1): the open group is fetched on mount and on Refresh only; a period click
// fetches the closed group alone (include=closed), so it never re-reads quotes or brokers.
import { useEffect, useRef, useState } from "react";
import { Layers, RefreshCw, AlertTriangle } from "lucide-react";

const PERIODS = [
  { key: "today", label: "Today" },
  { key: "7d", label: "7D" },
  { key: "30d", label: "30D" },
  { key: "90d", label: "90D" },
];

export function fmtPnl(v) {
  if (v === null || v === undefined || Number.isNaN(Number(v))) return "—";
  const n = Number(v);
  const abs = Math.abs(n).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `${n > 0 ? "+" : n < 0 ? "-" : ""}₹${abs}`;
}

function pnlColor(v) {
  if (v > 0) return "text-green-400";
  if (v < 0) return "text-red-400";
  return "text-zinc-300";
}

function Caveats({ items }) {
  const shown = items.filter(([n]) => n > 0);
  if (!shown.length) return null;
  return (
    <div className="mt-2 text-[11px] text-yellow-500/80 space-y-0.5">
      {shown.map(([n, text]) => <div key={text}>{n} {text}</div>)}
    </div>
  );
}

function Errors({ errors }) {
  if (!errors?.length) return null;
  return (
    <div className="mt-2 text-[11px] text-red-400 space-y-0.5" data-testid="ps-broker-errors">
      {errors.map((e, i) => (
        <div key={`${e.broker}-${i}`} className="flex items-start gap-1">
          <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
          <span>{e.broker}: {e.error || "could not be read"}</span>
        </div>
      ))}
    </div>
  );
}

export function SummaryCell({ title, cell, testId, caveats = [], extra = null }) {
  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-4" data-testid={testId}>
      <div className="overline text-zinc-500 mb-1">{title}</div>
      {!cell ? (
        <div className="text-sm text-zinc-600">—</div>
      ) : cell.available === false ? (
        <>
          <div className="text-sm font-section text-zinc-400">{cell.failed ? "Could not be computed" : "Not available"}</div>
          <div className="text-xs text-zinc-600 mt-1">{cell.reason}</div>
          <Errors errors={cell.errors} />
          {extra}
        </>
      ) : (
        <>
          <div className="flex items-baseline justify-between gap-2">
            <div className="text-xl font-mono-data font-bold text-white">{cell.count}</div>
            <div className={`text-sm font-mono-data font-bold ${pnlColor(cell.pnl)}`}>{fmtPnl(cell.pnl)}</div>
          </div>
          <div className="text-xs font-mono-data mt-1 flex flex-wrap gap-x-3">
            <span className="text-green-400">{cell.positive} positive</span>
            <span className="text-red-400">{cell.negative} negative</span>
            <span className="text-zinc-400">{cell.flat} flat</span>
            {cell.unknown > 0 && <span className="text-zinc-500">{cell.unknown} unknown</span>}
          </div>
          {cell.note && <div className="mt-1 text-[11px] text-zinc-600" data-testid={`${testId}-note`}>{cell.note}</div>}
          {cell.partial && <div className="mt-1 text-[11px] text-yellow-500/80">Partial: some brokers could not be read</div>}
          <Errors errors={cell.errors} />
          <Caveats items={caveats} />
          {extra}
        </>
      )}
    </div>
  );
}

export function fmtAsOf(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleTimeString("en-IN", { timeZone: "Asia/Kolkata", hour: "2-digit", minute: "2-digit", hour12: false });
}

const URL_BASE = "/api/analytics/position-summary";

export default function PositionSummary() {
  const [period, setPeriod] = useState("today");
  const [openData, setOpenData] = useState(null);     // {paper, live, as_of}
  const [closedData, setClosedData] = useState(null); // {paper, live, period}
  const [openError, setOpenError] = useState(false);
  const [closedError, setClosedError] = useState(false);
  const [openLoading, setOpenLoading] = useState(true);
  const [closedLoading, setClosedLoading] = useState(true);
  // Only the latest request of each group may update it (a slow older answer is dropped).
  const openReq = useRef(0);
  const closedReq = useRef(0);
  const mounted = useRef(false);

  // include: "all" (mount, Refresh) or "closed" (period click). The open group is never
  // fetched on a period click.
  async function load(p, include) {
    const wantOpen = include === "all";
    const oid = wantOpen ? ++openReq.current : null;
    const cid = ++closedReq.current;
    if (wantOpen) { setOpenLoading(true); setOpenError(false); }
    setClosedLoading(true);
    setClosedError(false);
    const openLive = () => wantOpen && oid === openReq.current;
    const closedLive = () => cid === closedReq.current;
    try {
      const url = include === "all" ? `${URL_BASE}?period=${p}` : `${URL_BASE}?period=${p}&include=${include}`;
      const r = await fetch(url, { credentials: "include" });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const body = await r.json();
      if (openLive()) {
        if (body?.open) setOpenData({ ...body.open, as_of: body.as_of });
        else { setOpenError(true); setOpenData(null); }
      }
      if (closedLive()) {
        if (body?.period === p && body?.closed) setClosedData({ ...body.closed, period: body.period });
        else { setClosedError(true); setClosedData(null); }
      }
    } catch (e) {
      if (openLive()) { setOpenError(true); setOpenData(null); }
      if (closedLive()) { setClosedError(true); setClosedData(null); }
    } finally {
      if (openLive()) setOpenLoading(false);
      if (closedLive()) setClosedLoading(false);
    }
  }

  useEffect(() => {
    if (!mounted.current) { mounted.current = true; load(period, "all"); }
    else load(period, "closed");
  }, [period]);  // eslint-disable-line react-hooks/exhaustive-deps

  const loading = openLoading || closedLoading;
  const error = openError || closedError
    ? `Position summary could not be loaded${openError && !closedError ? " (open positions)" : !openError ? " (closed positions)" : ""}.`
    : null;
  const data = { open: openData, closed: closedData, period: closedData?.period };
  const asOf = fmtAsOf(openData?.as_of);
  const op = data?.open?.paper;
  const ol = data?.open?.live;
  const cp = data?.closed?.paper;
  const cl = data?.closed?.live;
  const sup = cl?.bracket_booked;
  // The closed group is labelled from the answer's own period, never from the selector.
  const periodLabel = PERIODS.find((x) => x.key === data?.period)?.label || "—";

  return (
    <section data-testid="position-summary">
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <Layers className="w-4 h-4 text-zinc-400" />
        <span className="font-section text-sm text-zinc-300">Position Summary</span>
        <button
          onClick={() => load(period, "all")}
          className="ml-auto text-xs text-zinc-600 hover:text-zinc-400 flex items-center gap-1"
          aria-label="Refresh position summary"
        >
          <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>
      {error && <div className="text-xs text-red-400 mb-2" data-testid="ps-error">{error}</div>}

      <div className="space-y-4">
        <div>
          <div className="text-xs text-zinc-500 mb-2">
            Open — unrealised, as of now (marks may be delayed)
            {asOf && <span className="text-zinc-600" data-testid="ps-open-asof"> · fetched {asOf} IST, refresh to update</span>}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <SummaryCell title="Paper · open" cell={op} testId="ps-open-paper"
              caveats={[[op?.stale_count, "at the last real mark (no fresh quote; P&L an estimate, counted unknown)"],
                        [op?.simulated_count, "marked on a simulated option chain"]]} />
            <SummaryCell title="Live · open (broker-reported)" cell={ol} testId="ps-open-live"
              extra={ol?.available !== false && ol?.broker_count === 0
                ? <div className="mt-1 text-[11px] text-zinc-600">No live broker connected</div> : null} />
          </div>
        </div>

        <div>
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <span className="text-xs text-zinc-500">Closed — realised ({periodLabel}, IST)</span>
            <div className="ml-auto flex items-center gap-1">
              {PERIODS.map((p) => (
                <button
                  key={p.key}
                  onClick={() => setPeriod(p.key)}
                  className={`px-2 py-0.5 text-[11px] font-section rounded-sm transition-colors ${
                    period === p.key ? "bg-white text-black" : "text-zinc-400 hover:text-white bg-zinc-900 border border-zinc-800"
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <SummaryCell title="Paper · closed" cell={cp} testId="ps-closed-paper"
              caveats={[[cp?.estimated_count, "booked without a quote (P&L estimated)"],
                        [cp?.simulated_count, "on a simulated option chain"]]}
              extra={cp?.truncated
                ? <div className="mt-1 text-[11px] text-yellow-500/80">Too many closes: only the first rows were counted</div>
                : null} />
            <SummaryCell title="Live · closed" cell={cl} testId="ps-closed-live"
              extra={sup && sup.count > 0 ? (
                <div className="mt-2 border-t border-zinc-800 pt-2 text-[11px] text-zinc-500" data-testid="ps-bracket-booked">
                  <div>
                    Bracket exits with a booked P&L: {sup.count} ({sup.positive} positive, {sup.negative} negative,
                    {" "}{sup.flat} flat) {fmtPnl(sup.pnl)}
                  </div>
                  <div className="text-zinc-600">{sup.note}</div>
                  {sup.truncated && (
                    <div className="text-yellow-500/80" data-testid="ps-bracket-truncated">
                      Too many bracket exits: only the first rows were counted
                    </div>
                  )}
                </div>
              ) : null} />
          </div>
        </div>
      </div>
    </section>
  );
}
